import { ChangeDetectorRef, Component } from '@angular/core'
import { CommonModule, NgOptimizedImage } from '@angular/common'
import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { BehaviorSubject, Observable, take } from 'rxjs'
import { ApprovalFlowService } from '@app/features/scope-overview/service/approval-flow.service'
import { FormulaService } from '@shared/services/formula.service'
import { LibraryDeliverableEntryDetailsService } from '@app/features/library-management/services/library-deliverable-entry-details.service'
import { ScopeConfigurationService } from '@app/features/scope-overview/service/scope-configuration.service'
import { ScopeOverviewNavigationService } from '@app/features/scope-overview/service/scope-overview-navigation.service'
import { ScopeOverviewTab } from '@app/features/scope-overview/enum/scope-overview-tab.enum'
import { ChangeRequestActions } from '@app/features/scope-overview/change-request/store/actions/change-request.actions'
import { ChangeRequestSelectors } from '@app/features/scope-overview/change-request/store/selectors/change-request.selectors'
import { ChangeRequest } from '@app/core/model/change-request.model'
import { ChangeItemType } from '@app/features/scope-overview/change-request/model/change-item-type.enum'
import { ScenarioCategory } from '@app/features/scope-overview/model/scenario-category.model'
import { ScopeConfiguration } from '@core/model/scope-version'
import { ScopeConfigurationComponent } from './scope-configuration.component'
import { ScopeUiCheckboxComponent } from '@shared/components/ui-components/scope-ui-checkbox/scope-ui-checkbox.component'
import { ScopeUiDatepickerComponent } from '@shared/components/ui-components/scope-ui-datepicker/scope-ui-datepicker.component'
import { ScopeUiDropdownComponent } from '@shared/components/ui-components/scope-ui-dropdown/scope-ui-dropdown.component'
import { ScopeUiInputComponent } from '@shared/components/ui-components/scope-ui-input/scope-ui-input.component'
import { FormulaBuilderComponent } from '@shared/components/formula-builder/formula-builder.component'
import { SharedModule } from '@shared/shared.module'
import { LibraryManagementNgrxModule } from '@app/features/library-management/library-management-ngrx.module'
import { getConfigurationWithEmptyAnswerValues } from '@app/features/scope-overview/utils/get-configuation-with-empty-answer-values.util'
import { getQuestionValueByType } from '@app/features/scope-overview/utils/get-question-value-by-type.util'
import { QuestionType } from '@core/model/enums/question-type.enum'

@Component({
  selector: 'scope-configuration-cr',
  templateUrl: './scope-configuration.component.html',
  styleUrls: ['./scope-configuration.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    ScopeUiInputComponent,
    FormulaBuilderComponent,
    ScopeUiDatepickerComponent,
    ScopeUiDropdownComponent,
    NgOptimizedImage,
    LibraryManagementNgrxModule,
    ScopeUiCheckboxComponent,
  ],
})
export class ScopeConfigurationCRComponent extends ScopeConfigurationComponent {
  currentChangeRequest$: Observable<ChangeRequest | undefined> = this.store.select(
    ChangeRequestSelectors.selectCurrentChangeRequest
  )
  changeItemId?: string
  private scopeConfiguration?: ScopeConfiguration
  private scopeComponentModifiers?: Record<number, number>

  private crSaveLoading$ = new BehaviorSubject<boolean>(false)
  private crSubmitLoading$ = new BehaviorSubject<boolean>(false)
  override saveLoading$ = this.crSaveLoading$.asObservable()
  override submitLoading$ = this.crSubmitLoading$.asObservable()

  constructor(
    store: Store,
    private actions$: Actions,
    cdr: ChangeDetectorRef,
    formulaService: FormulaService,
    approvalFlowService: ApprovalFlowService,
    libraryDeliverableEntryDetailsService: LibraryDeliverableEntryDetailsService,
    private scopeConfigurationService: ScopeConfigurationService,
    private scopeOverviewNavigationService: ScopeOverviewNavigationService
  ) {
    super(store, cdr, formulaService, approvalFlowService, libraryDeliverableEntryDetailsService)
  }

  override ngOnInit() {
    super.ngOnInit()
    this.scopeConfiguration = this.scopeVersion.configuration
    this.scopeComponentModifiers = { ...(this.scopeVersion.componentModifierValues || {}) }
    this.currentChangeRequest$.pipe(this.destroy$()).subscribe((changeRequest: ChangeRequest | undefined) => {
      this.changeRequestId = changeRequest?.id
      this.editChangeRequestByScenarioAgree = false
      this.changeItemId = this.getScenarioConfigChange(changeRequest)?.uuid

      if (this.scenarioInitialized && changeRequest) {
        const scenarioConfigChange = this.getScenarioConfigChange(changeRequest)
        this.applyChangeItemValues(scenarioConfigChange)
      }

      this.cdr.detectChanges()
    })
  }

  override initializeScenario() {
    super.initializeScenario()
    this.currentChangeRequest$.pipe(take(1)).subscribe((changeRequest) => {
      const scenarioConfigChange = this.getScenarioConfigChange(changeRequest)
      this.applyChangeItemValues(scenarioConfigChange)
    })
  }

  private getScenarioConfigChange(changeRequest?: ChangeRequest) {
    return changeRequest?.changes?.find((change) => change.type === ChangeItemType.SCENARIO_CONFIGURATION)
  }

  private applyChangeItemValues(scenarioConfigChange?: any): void {
    const savedConfiguration = scenarioConfigChange?.data?.scenarioConfiguration?.new
    const savedComponentModifiers = scenarioConfigChange?.data?.componentModifiers?.new

    if (!savedConfiguration || !this.scenario?.categories || !this.configuration) return

    if (savedComponentModifiers) {
      this.scopeVersion.componentModifierValues = { ...savedComponentModifiers }
    }

    const categoriesById = new Map(this.scenario.categories.map((category) => [category.id, category]))

    Object.entries(savedConfiguration).forEach(([categoryIdStr, questionsData]) => {
      const categoryId = +categoryIdStr
      const category = categoriesById.get(categoryId)

      if (!category?.questions || typeof questionsData !== 'object') return

      const categoryConfig = { ...(this.configuration[categoryId] || {}) }
      const questionsById = new Map(category.questions.map((question) => [question.id, question]))

      Object.entries(questionsData).forEach(([questionIdStr, answerValue]) => {
        if (answerValue == null) return

        const questionId = +questionIdStr
        const question = questionsById.get(questionId)
        if (!question) return

        categoryConfig[questionId] = answerValue
        question.value = getQuestionValueByType(question.type, answerValue)
        this.questionStates[questionId]?.control?.setValue(question.value)

        if (question.type === QuestionType.DELIVERABLE && question.deliverablePrompt) {
          this.setupDeliverablePrompt(question)
        }
        if (question.type === QuestionType.FEE) {
          this.setupFeePrompt(question)
        }
      })

      this.configuration = {
        ...this.configuration,
        [categoryId]: categoryConfig,
      }
    })

    this.cdr.detectChanges()
  }

  override get editable(): boolean {
    return this.changeRequestId != null || super.editable
  }

  override set editable(value: boolean) {
    super.editable = value
  }

  override get configurationSubmitted(): boolean {
    return this.changeRequestId != null ? false : super.configurationSubmitted
  }

  override set configurationSubmitted(value: boolean) {
    super.configurationSubmitted = value
  }

  override save(category: ScenarioCategory, selectNext?: boolean) {
    if (!this.changeRequestId) {
      super.save(category, selectNext)
      return
    }

    this.formSubmitted = true
    this.configuration = getConfigurationWithEmptyAnswerValues(category, this.configuration)
    this.updateConfiguration(this.configuration)

    if (this.isCategoryValid(category)) {
      const nextCategoryId = this.getNextCategoryId(category.id)

      this.crSaveLoading$.next(true)
      this.saveConfigurationToChangeRequest(() => {
        this.crSaveLoading$.next(false)
        if (nextCategoryId) this.categoryStates[nextCategoryId].enabled = true
        this.categoryStates[category.id].updated = false
        if (selectNext) this.selectCategory(nextCategoryId)
        this.editDeliverablePromptAgree = false
        this.cdr.detectChanges()
      })
    }
  }

  override submitConfiguration() {
    if (!this.changeRequestId) {
      super.submitConfiguration()
      return
    }

    this.crSubmitLoading$.next(true)
    this.scopeConfigurationService.getModifiers(this.scenario.id).subscribe((modifiers) => {
      const componentModifiers: Record<number, number> = { ...this.scopeComponentModifiers }

      modifiers.forEach((modifier) => {
        const result = this.formulaService.calculate(modifier.formula)
        if (typeof result === 'number') {
          componentModifiers[modifier.id] = result
        }
      })

      this.saveConfigurationToChangeRequest(() => {
        this.store.dispatch(
          ChangeRequestActions.generateChangesFromScenarioConfig({
            scopeId: this.scopeVersion.identity.id,
            changeRequestId: this.changeRequestId!,
          })
        )

        this.actions$
          .pipe(
            ofType(
              ChangeRequestActions.generateChangesFromScenarioConfigSuccess,
              ChangeRequestActions.generateChangesFromScenarioConfigFail
            ),
            take(1)
          )
          .subscribe(() => {
            this.crSubmitLoading$.next(false)
            this.scopeOverviewNavigationService.selectTabAndReload(
              ScopeOverviewTab.SCOPE,
              this.scopeVersion.identity.id
            )
          })
      }, componentModifiers)
    })
  }

  private saveConfigurationToChangeRequest(onSuccess?: () => void, componentModifiers?: Record<number, number>): void {
    this.store.dispatch(
      ChangeRequestActions.saveScenarioConfig({
        scopeId: this.scopeVersion.identity.id,
        changeRequestId: this.changeRequestId!,
        configuration: this.configuration,
        oldConfiguration: this.scopeConfiguration,
        componentModifiers: componentModifiers ?? this.scopeComponentModifiers ?? {},
        oldComponentModifiers: this.scopeComponentModifiers ?? {},
        changeItemId: this.changeItemId,
      })
    )

    this.actions$.pipe(ofType(ChangeRequestActions.saveScenarioConfigSuccess), take(1)).subscribe(({ changeItem }) => {
      this.updateChangeItemId(changeItem?.uuid)
      onSuccess?.()
    })

    this.actions$.pipe(ofType(ChangeRequestActions.saveScenarioConfigFail), take(1)).subscribe(() => {
      this.crSaveLoading$.next(false)
      this.crSubmitLoading$.next(false)
    })
  }

  private updateChangeItemId(uuid?: string): void {
    if (uuid && !this.changeItemId) {
      this.changeItemId = uuid
    }
  }
}
